/**
 * Grid grouping by classification (HP-V, Pedro 2026-10-19; Securities too since HP-V4):
 * group rows by an ORDERED list of levels, each one tier of one lens of a classification
 * view (e.g. Asset Class, then Region, then Sub-Class), into nested groups with a row count
 * and, where rows carry a value, a value subtotal.
 *
 * It runs in the browser over the rows left after the column filters, so every subtotal
 * covers exactly the rows shown (Pedro chose filters now, V3). Rules:
 *   * Non-classified rows, cash lines and loans are never split by the levels: they stay their
 *     own top-level groups, "Non-Classified Securities", "Cash", then "Loans" (HP-L), after the
 *     classified groups.
 *   * A row with no reading on a level's lens goes to "No <lens> reading" (absent by design
 *     for a scoped lens, C-289); a reading that stops above the level's tier goes to
 *     "No <tier>" (e.g. Equities with no Sub-Class). Both sort after the values.
 *   * Values are listed in the taxonomy order set in Classification (the path's sort orders,
 *     parents first), then by label.
 *   * With no level, the classified rows sit in one "Securities" group when a special group
 *     also exists, otherwise ungrouped.
 * Pure (no React), so it can be checked on its own. One module for both grids (§3h).
 */
import type { ConsolidatedRow, HoldingLine } from '../api/portfolios';

export const NON_CLASSIFIED = 'Non-Classified Securities';
export const CASH = 'Cash';
export const LOANS = 'Loans';

/** A lens of the view, as the grids know it. */
export interface GroupLens { code: string; name: string; tiers: { level: number; name: string }[] }
export interface GroupLevel { code: string; tier: number }

/** How the grouping reads a row. */
export interface RowAccess<T> {
  /** The row's path on a lens, top tier first (undefined: no reading on that lens). */
  path: (row: T, code: string) => string[] | undefined;
  /** The same path's taxonomy sort orders. */
  orders: (row: T, code: string) => number[];
  /** A row kept out of the levels: a cash line, a loan, or a non-classified security. */
  special: (row: T) => 'cash' | 'loan' | 'non' | null;
  /** The row's value for the subtotals (0 when the grid has no value). */
  value: (row: T) => number;
}

export interface GroupNode<T = HoldingLine> {
  /** Stable id: the labels from the top, joined (used to remember folded groups). */
  id: string;
  label: string;
  depth: number;
  value: number;          // Σ value of the rows inside
  lineCount: number;
  children: GroupNode<T>[];  // sub-groups (empty at the last level)
  lines: T[];                // the rows (only at the last level)
}

/** "asset_class:2" ⇄ { code, tier }. */
export const levelKey = (l: GroupLevel) => `${l.code}:${l.tier}`;
export function parseLevel(key: string): GroupLevel | null {
  const i = key.lastIndexOf(':');
  const tier = Number(key.slice(i + 1));
  return i > 0 && Number.isInteger(tier) && tier > 0 ? { code: key.slice(0, i), tier } : null;
}

/** The levels that exist in this view (a level of another view's lens is ignored). */
export function validLevels(keys: string[], lenses: GroupLens[]): GroupLevel[] {
  const out: GroupLevel[] = [];
  for (const k of keys) {
    const l = parseLevel(k);
    const lens = l && lenses.find((x) => x.code === l.code);
    if (l && lens && lens.tiers.some((t) => t.level === l.tier)) out.push(l);
  }
  return out;
}

/** The text shown for a level in the Group-by list. */
export function levelLabel(l: GroupLevel, lenses: GroupLens[]): string {
  const lens = lenses.find((x) => x.code === l.code);
  const tier = lens?.tiers.find((t) => t.level === l.tier);
  if (!lens || !tier) return levelKey(l);
  return lens.tiers.length === 1 || tier.name === lens.name ? lens.name : `${lens.name} › ${tier.name}`;
}

/** The Group-by list's options, one group per lens. */
export function levelOptions(lenses: GroupLens[]) {
  return lenses.map((lz) => ({
    group: lz.name,
    items: lz.tiers.map((t) => {
      const l = { code: lz.code, tier: t.level };
      return { value: levelKey(l), label: levelLabel(l, lenses) };
    }),
  }));
}

// Sort key: [bucket (0 value, 1 "No tier", 2 "No reading"), ...orders, label].
type Key = { label: string; rank: (number | string)[] };

function keyFor<T>(row: T, l: GroupLevel, lens: GroupLens, acc: RowAccess<T>): Key {
  const path = acc.path(row, l.code);
  if (!path || path.length === 0) return { label: `No ${lens.name} reading`, rank: [2] };
  if (path.length < l.tier) {
    const tierName = lens.tiers.find((t) => t.level === l.tier)?.name ?? `tier ${l.tier}`;
    return { label: `No ${tierName}`, rank: [1] };
  }
  const orders = acc.orders(row, l.code).slice(0, l.tier);
  return { label: path[l.tier - 1], rank: [0, ...orders, path[l.tier - 1]] };
}

function cmp(a: (number | string)[], b: (number | string)[]): number {
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const x = a[i], y = b[i];
    if (x === undefined) return -1;
    if (y === undefined) return 1;
    if (x === y) continue;
    if (typeof x === 'number' && typeof y === 'number') return x - y;
    return String(x).localeCompare(String(y));
  }
  return 0;
}

function leaf<T>(id: string, label: string, depth: number, rows: T[], acc: RowAccess<T>): GroupNode<T> {
  return { id, label, depth, value: rows.reduce((s, r) => s + acc.value(r), 0),
    lineCount: rows.length, children: [], lines: rows };
}

function split<T>(rows: T[], levels: GroupLevel[], lenses: GroupLens[],
  depth: number, parentId: string, acc: RowAccess<T>): GroupNode<T>[] {
  const l = levels[depth];
  const lens = lenses.find((x) => x.code === l.code)!;
  const buckets = new Map<string, { key: Key; rows: T[] }>();
  for (const r of rows) {  // keeps the incoming (sorted) row order inside each bucket
    const k = keyFor(r, l, lens, acc);
    const b = buckets.get(k.label);
    if (b) { b.rows.push(r); if (cmp(k.rank, b.key.rank) < 0) b.key = k; }
    else buckets.set(k.label, { key: k, rows: [r] });
  }
  return [...buckets.values()]
    .sort((a, b) => cmp(a.key.rank, b.key.rank))
    .map(({ key, rows: rs }) => {
      const id = `${parentId}\u0001${key.label}`;
      if (depth === levels.length - 1) return leaf(id, key.label, depth, rs, acc);
      const children = split(rs, levels, lenses, depth + 1, id, acc);
      return { ...leaf(id, key.label, depth, rs, acc), children, lines: [] };
    });
}

/** The top-level groups, in display order. `rows` must already be filtered and sorted. */
export function groupRows<T>(rows: T[], levels: GroupLevel[], lenses: GroupLens[], acc: RowAccess<T>): {
  groups: GroupNode<T>[]; ungrouped: T[];
} {
  const cash = rows.filter((r) => acc.special(r) === 'cash');
  const loans = rows.filter((r) => acc.special(r) === 'loan');
  const non = rows.filter((r) => acc.special(r) === 'non');
  const cls = rows.filter((r) => acc.special(r) === null);
  const specials = [
    ...(non.length ? [leaf(NON_CLASSIFIED, NON_CLASSIFIED, 0, non, acc)] : []),
    ...(cash.length ? [leaf(CASH, CASH, 0, cash, acc)] : []),
    ...(loans.length ? [leaf(LOANS, LOANS, 0, loans, acc)] : []),
  ];
  if (levels.length === 0) {
    if (specials.length === 0) return { groups: [], ungrouped: cls };
    return { groups: [...(cls.length ? [leaf('Securities', 'Securities', 0, cls, acc)] : []), ...specials], ungrouped: [] };
  }
  return { groups: [...split(cls, levels, lenses, 0, '', acc), ...specials], ungrouped: [] };
}

/** How the grouping reads a portfolio's holding line. */
export const HOLDING_ACCESS: RowAccess<HoldingLine> = {
  path: (ln, code) => ln.classifications[code],
  orders: (ln, code) => ln.orders?.[code] ?? [],
  special: (ln) => (ln.is_cash ? 'cash' : ln.is_loan ? 'loan' : ln.non_classified ? 'non' : null),
  value: (ln) => (ln.value_eur == null ? 0 : Number(ln.value_eur)),
};

/** How the grouping reads a consolidated View portfolio row (the grid and, TP-PDF, the trade
 *  document's Before and after, which follows the grouping on screen at export). */
export const CONSOLIDATED_ACCESS: RowAccess<ConsolidatedRow> = {
  path: (r, code) => r.classifications[code],
  orders: (r, code) => r.orders?.[code] ?? [],
  special: (r) => (r.is_cash ? 'cash' : r.is_loan ? 'loan' : r.non_classified ? 'non' : null),
  value: () => 0,
};

/** The rows in the grouping's display order, each with its group path (top level first). */
export function flattenGroups<T>(groups: GroupNode<T>[], ungrouped: T[]): { row: T; path: string[] }[] {
  const out: { row: T; path: string[] }[] = [];
  const walk = (g: GroupNode<T>, path: string[]) => {
    const p = [...path, g.label];
    if (g.children.length) g.children.forEach((ch) => walk(ch, p));
    else g.lines.forEach((row) => out.push({ row, path: p }));
  };
  groups.forEach((g) => walk(g, []));
  ungrouped.forEach((row) => out.push({ row, path: [] }));
  return out;
}

/** A portfolio's lines grouped (View portfolio). */
export function groupLines(lines: HoldingLine[], levels: GroupLevel[], lenses: GroupLens[]) {
  return groupRows(lines, levels, lenses, HOLDING_ACCESS);
}

/** A group row's background by depth (both grids): darker at the top level. */
const GROUP_BG = ['var(--mantine-color-gray-2)', 'var(--mantine-color-gray-1)', 'var(--mantine-color-gray-0)'];
export const groupRowBg = (depth: number) => GROUP_BG[Math.min(depth, GROUP_BG.length - 1)];

/** Every group id in the tree (for "Fold all"). */
export function allGroupIds<T>(groups: GroupNode<T>[]): string[] {
  return groups.flatMap((g) => [g.id, ...allGroupIds(g.children)]);
}
